/**
 * Lo que el vendedor envía al crear/editar un anuncio (`listings-create` / `listings-update`).
 * El servidor añade el resto (vendedor, tokens de búsqueda, ruta de categoría, moderación, +18).
 * Límites de la 1.0: título 120, descripción 4000, 10 fotos (50 PRO), vídeo 30 s/50 MB.
 */
import { z } from 'zod';
import { CurrencySchema, LanguageCodeSchema } from '../firestore/primitives.js';
import { PRODUCT_CONDITIONS } from '../firestore/products.js';

/** Foto subida por el propio vendedor a Storage (uploads/{uid}/…) o ya existente en el anuncio. */
export const ListingPhotoUrlSchema = z
  .string()
  .url()
  .max(2000)
  .refine((u) => u.startsWith('https://'), 'https');

export const ListingInputSchema = z.object({
  /** Métrica 10b: el vendedor usó «Rellenar con IA» (listingFromPhotos) en este anuncio. */
  ai_assisted: z.boolean().optional(),
  title: z.string().trim().min(3).max(120),
  description: z.string().trim().max(4000).default(''),
  /** Decimal en unidades de la moneda, como la 1.0 (12.5, 850). */
  price: z.number().min(0).max(1_000_000),
  currency: CurrencySchema,
  category_id: z.string().min(1).max(60),
  condition: z.enum(PRODUCT_CONDITIONS),
  photos: z.array(ListingPhotoUrlSchema).min(1).max(50),
  video_url: ListingPhotoUrlSchema.nullable().default(null),
  brand: z.string().trim().max(60).default(''),
  size: z.string().trim().max(12).default(''),
  color: z.string().trim().max(40).default(''),
  material: z.string().trim().max(40).default(''),
  pattern: z.string().trim().max(40).default(''),
  weight_tier: z.number().int().min(1).max(5).default(1),
  length_cm: z.number().int().min(1).max(300).nullable().default(null),
  width_cm: z.number().int().min(1).max(300).nullable().default(null),
  height_cm: z.number().int().min(1).max(300).nullable().default(null),
  offers_enabled: z.boolean().default(true),
  /** Solo PRO (el servidor lo ignora si no lo es, como `sanitizeProductProFields` de la 1.0). */
  free_shipping: z.boolean().default(false),
  stock: z.number().int().min(1).max(9999).default(1),
  original_price: z.number().min(0).nullable().default(null),
  original_language: LanguageCodeSchema,
  /** Palabras clave extra para la búsqueda (1.0 search_keywords). */
  search_keywords: z.string().trim().max(300).default(''),
  vehicle_make: z.string().trim().max(60).default(''),
  vehicle_model: z.string().trim().max(60).default(''),
  /** Vacío = todos los países. */
  visible_countries: z
    .array(z.string().regex(/^[A-Z]{2}$/))
    .max(40)
    .default([]),
  city: z.string().trim().max(120).default(''),
  region: z.string().trim().max(120).default(''),
});
export type ListingInput = z.infer<typeof ListingInputSchema>;
export type ListingInputRaw = z.input<typeof ListingInputSchema>;
